import React, { Fragment, ReactNode, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ConfirmModal from './ConfirmModal';
import { SlideoverProvider } from './SideoverContext';

export type SlideoverChildrenOptions = {
  setConfirmOnClose: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: (options?: { ignoreConfirmation?: boolean }) => void;
};

export type SlideoverProps = {
  children: ReactNode | ((options: SlideoverChildrenOptions) => ReactNode);
  header?: { title: string; description: string };
  open: boolean;
  onClose(value: boolean): void;
  panelClassName?: string;
  isFullScreenDefault?: boolean;
  className?: string;
};
export default function Slideover({
  className,
  children,
  header,
  open,
  onClose,
  panelClassName = 'max-w-md',
  isFullScreenDefault = false,
}: SlideoverProps) {
  const [confirmOnClose, setConfirmOnClose] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(isFullScreenDefault);

  return (
    <>
      <Transition.Root show={Boolean(open)} as={Fragment}>
        <Dialog
          as="div"
          className={`relative z-10 ${className}`}
          onClose={(value) => {
            if (confirmOnClose) {
              setShowConfirmModal(true);
            } else {
              onClose(value);
            }
          }}
        >
          <div className="fixed inset-0 " />

          <div className="fixed inset-0  overflow-hidden">
            <div className="absolute inset-0   overflow-hidden">
              <div
                className={`pointer-events-none fixed inset-y-0 right-0 flex max-w-full ${
                  isFullScreen ? '' : 'w-md:pl-10'
                }  `}
              >
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel
                    className={`pointer-events-auto w-screen ${panelClassName} ${
                      isFullScreen ? '!max-w-none' : ''
                    } `}
                  >
                    <SlideoverProvider isFullScreen={isFullScreen}>
                      <div className="flex h-full overflow-x-hidden flex-col overflow-y-scroll bg-white shadow-xl">
                        {header ? (
                          <div className="bg-primary-700 py-6 px-4 sm:px-6">
                            <div className="flex items-center justify-between">
                              <Dialog.Title className="text-base font-semibold leading-6 text-white">
                                {header.title}
                              </Dialog.Title>
                              <div className="ml-3 flex h-7 items-center gap-x-2">
                                <button
                                  type="button"
                                  className="rounded-md bg-primary-700 text-primary-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                  onClick={() => setIsFullScreen(!isFullScreen)}
                                >
                                  <span className="sr-only">
                                    {isFullScreen ? 'Minimize' : 'Maximize'}
                                  </span>
                                  {isFullScreen ? (
                                    <ArrowsPointingInIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <ArrowsPointingOutIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="rounded-md bg-primary-700 text-primary-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                  onClick={() => {
                                    if (confirmOnClose) {
                                      setShowConfirmModal(true);
                                    } else {
                                      onClose(false);
                                    }
                                  }}
                                >
                                  <span className="sr-only">Close panel</span>
                                  <XMarkIcon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                            </div>
                            <div className="mt-1">
                              <p className="text-sm text-primary-300">
                                {header.description}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-end items-center pt-4 pr-4">
                            <button
                              type="button"
                              className="rounded-md  text-primary-700 hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-white"
                              onClick={() => onClose(false)}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        )}
                        <div className="relative flex-1 py-6 px-4 sm:px-6">
                          {typeof children === 'function'
                            ? children({
                                setConfirmOnClose,
                                onClose: (options) => {
                                  if (options?.ignoreConfirmation) {
                                    onClose(false);
                                    return;
                                  }
                                  if (confirmOnClose) {
                                    setShowConfirmModal(true);
                                  } else {
                                    onClose(false);
                                  }
                                },
                              })
                            : children}
                        </div>
                      </div>
                    </SlideoverProvider>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <ConfirmModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        onConfirm={() => {
          onClose(false);
          setConfirmOnClose(false);
        }}
      />
    </>
  );
}
